import { EventContext } from './EventContext';

export class EventDispatcher<T extends { [K in keyof T]: any[] }>
{
    private readonly listeners: ListenerRegistry<T> = {};

    /**
     * Subscribes to an event of the given type.
     *
     * @returns An object that can be used to unsubscribe from the event.
     */
    public on<K extends keyof T>(event: K, callback: Callback<T, K>): IDisposable
    {
        return this._registerListener(event, callback);
    }

    /**
     * Subscribes to an event of the given type, but only for a single
     * occurrence.
     *
     * @returns An object that can be used to unsubscribe from the
     *          event. If this object goes out of scope, the event
     *          subscription is canceled immediately.
     */
    public once<K extends keyof T>(event: K, callback: Callback<T, K>): IDisposable
    {
        const subscription: IDisposable = this._registerListener(event, (...args: [...T[K], EventContext]): CallbackResult => {
            subscription.dispose();
            return callback(...args);
        });

        return subscription;
    }

    /**
     * Dispatches an event to all active subscriptions.
     */
    protected dispatch<K extends keyof T>(event: K, ...args: [...T[K]]): EventContext
    {
        const list: ListenerRegistry<T>[K] = this.listeners[event] ?? [];
        const context: EventContext        = new EventContext();

        for (const entry of list!) {
            entry(...[...args, context]);

            if (context.isCanceled) {
                break;
            }
        }

        return context;
    }

    /**
     * Dispatches an event to all active subscriptions.
     */
    protected async dispatchAsync<K extends keyof T>(event: K, ...args: [...T[K]]): Promise<EventContext>
    {
        const list: ListenerRegistry<T>[K] = this.listeners[event] ?? [];
        const context: EventContext        = new EventContext();

        for (const entry of list!) {
            await entry(...[...args, context]);

            if (context.isCanceled) {
                break;
            }
        }

        return context;
    }

    private _registerListener<K extends keyof T>(event: K, callback: Callback<T, K>): IDisposable
    {
        const dispose: () => void = () => {
            const list: ListenerRegistry<T>[K] = this.listeners[event];
            list?.splice(list.indexOf(callback), 1);
        };

        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }

        this.listeners[event].push(callback);

        return { dispose };
    }
}

type CallbackResult = any | Promise<any>;
type Callback<T extends { [K in keyof T]: any[] }, K extends keyof T> = (...args: [...T[K], EventContext]) => CallbackResult;
type ListenerRegistry<T extends { [K in keyof T]: any[] }> = {
    [K in keyof T]?: Array<Callback<T, K>>;
};
